import { composeReducers } from 'common/util';
import lodash, { get, keyBy, isEqual, unset } from 'lodash';
import files from './files';
import externalUsers from './externalUsers';
import usersConfiguration from './usersConfiguration';
import {
    LOAD_BINDER_USERS,
    LOAD_BINDER,
    LOADING_BINDER,
    FAILED_LOADING_BINDER,
    LOAD_DOCUMENT,
    BINDER_CONFIG_ERRORS,
    DECREMENT_COUNT_UNFILED_DOCUMENT,
    LOADING_BINDER_COUNT_UNFILED_DOCUMENTS,
    LOAD_BINDER_STATUS_OPTIONS,
    REMOVE_BINDER, 
    REMOVE_USERS,
} from 'binder/actions';

import {
    LOAD_BINDER_SETUP_FIELDS,
    NOTIFY_BINDER_SETUP_LOADED,
    UPDATE_BINDER_SETUP_FIELD_VALUE,
    WRITE_BINDER_SETUP_ERROR_MESSAGES,
    NOTIFY_BINDER_ACTIVATION_SUCCESS,
    BINDER_SETUP_WRITE_SAVING,
    SET_BINDER_NOTIFIED_USERS_LOADED_STATUS,
    LOAD_BINDER_NOTIFIED_USERS_SUCCESS,
    SAVE_BINDER_NOTIFIED_USERS_SUCCESS
} from 'binder/actions/binderSetup';

export function notifyBinderActivationSuccess(state, action) {
    if (!action || action.type !== NOTIFY_BINDER_ACTIVATION_SUCCESS) {
        return state;
    }

    return {
        ...state,
        binder: {
            ...state.binder,
            setup: {
                ...state.setup,
                shouldRedirect: true
            }
        }
    };
}

export function notifyBinderSetupLoaded(state, action) {
    if (!action || action.type !== NOTIFY_BINDER_SETUP_LOADED) {
        return state;
    }

    return {
        ...state,
        binder: {
            ...state.binder,
            setup: {
                ...state.setup,
                loaded: Boolean(action.payload)
            }
        }
    };
}

export function updateBinderSetupFieldValue(state, action) {
    if (!action || action.type !== UPDATE_BINDER_SETUP_FIELD_VALUE) {
        return state;
    }

    const { groupId, value } = action.payload;
    let fieldToUpdate = get(state, ['binder', 'setup', 'fields', groupId]);

    if (isEqual(fieldToUpdate.value, value)) {
        return state;
    } else {
        fieldToUpdate.value = value;
    }

    return {
        ...state,
        binder: {
            ...state.binder,
            setup: {
                ...state.binder.setup,
                fields: {
                    ...state.binder.setup.fields,
                    [groupId]: {
                        ...fieldToUpdate
                    }
                }
            }
        }
    };
}

export function loadBinderSetupFieldsData(state, action) {
    if (!action || action.type !== LOAD_BINDER_SETUP_FIELDS) {
        return state;
    }

    const {
        setupFields,
        descriptors
    } = action.payload;

    return {
        ...state,
        binder: {
            ...state.binder,
            setup: {
                ...state.setup,
                fields: keyBy(setupFields, 'key.groupId'),
                descriptors: keyBy(descriptors, 'id'),
                loaded: true
            }
        }
    };
}

export function writeBinderSetupErrors(state, action) {
    if (!action || action.type !== WRITE_BINDER_SETUP_ERROR_MESSAGES) {
        return state;
    }

    return {
        ...state,
        binder: {
            ...state.binder,
            setup: {
                ...state.binder.setup,
                errors: action.payload
            }
        }
    };
}

export function loadBinderUsers(state, action) {
    if (!action || action.type !== LOAD_BINDER_USERS) {
        return state;
    }

    const currentBinderUsers = get(state, 'binder.users', []);
    const loadedBinderUsers = action.payload;

    if (isEqual(currentBinderUsers, loadedBinderUsers)) {
        return state;
    } else {
        return {
            ...state,
            binder: {
                ...state.binder,
                users: loadedBinderUsers,
            }
        };
    }
}

export function loadBinder(state, action){
    if(!action || action.type !== LOAD_BINDER
        && action.type !== LOADING_BINDER
        && action.type !== FAILED_LOADING_BINDER){
        return state;
    }

    if (action.type === LOADING_BINDER) {
        return {
            ...state,
            app: {
                ...state.app,
                loadingBinder: true
            }
        };
    }

    if (action.type === FAILED_LOADING_BINDER) {
        return {
            ...state,
            app: {
                ...state.app,
                loadingBinder: false
            }
        };
    }

    const { binder, dataSources, isBinderWithSlimLayout } = action.payload;
    if(!binder){
        return {
            ...state,
            app: {
                ...state.app,
                loadingBinder: false
            }
        };
    }

    let { layout } = binder;
    layout = JSON.parse(layout);
    const folders = keyBy(get(layout, 'folders', []), 'id');
    const features = lodash.keyBy(binder.features, 'featureKey');

    return {
        ...state,
        app: {
            ...state.app,
            loadingBinder: false
        },
        binder: {
            ...state.binder,
            ...binder,
            isBinderWithSlimLayout,
            layout,
            folders,
            features,
        },
        dataSources: keyBy(dataSources, 'id')
    };
}

export function removeBinder(state, action){
    if(!action || action.type !== REMOVE_BINDER){
        return state;
    }

    const newState = { ...state };
    unset(newState, 'binder');
    return newState;
}

export function removeUsers(state, action){
    if(!action || action.type !== REMOVE_USERS){
        return state;
    }
    
    const newState = { ...state };
    unset(newState, 'users');
    return newState;
}


// To help preserve InstitutionTimezone for External Users
export function mapInstitutionTimezone(state, action) {
    if(!action || action.type !== LOAD_BINDER){
        return state;
    }

    const binderInstitutionTimezone = get(state, 'binder.institutionTimezone');
    return {
        ...state,
        me: {
            ...state.me,
            institutionTimezone: binderInstitutionTimezone
        }
    };
}

export function setCountUnfiledDocuments(state, action) {
    if (!action || action.type !== LOADING_BINDER_COUNT_UNFILED_DOCUMENTS) {
        return state;
    }
    return {
        ...state,
        binder: {
            ...state.binder,
            countUnfiledDocuments: action.payload
        }
    };
}

export function decrementUnfiledDocuments(state, action) {
    if (!action || action.type !== DECREMENT_COUNT_UNFILED_DOCUMENT) {
        return state;
    }

    return {
        ...state,
        binder: {
            ...state.binder,
            countUnfiledDocuments: state.binder.countUnfiledDocuments - 1
        }
    };
}

export function loadDocument(state, action) {
    if (!action || action.type !== LOAD_DOCUMENT) {
        return state;
    }

    const { document } = action.payload;

    const result = {
        ...state,
        document,
    };

    return result;
}

export function binderErrors(state, action) {
    if (!action || action.type !== BINDER_CONFIG_ERRORS) {
        return state;
    }

    let { errors } = action.payload;
    return {
        ...state,
        binderErrors: errors
    };

}

export function writeBindersSaving(state, action) {
    if (!action || action.type !== BINDER_SETUP_WRITE_SAVING) {
        return state;
    }
    return {
        ...state,
        binder: {
            ...state.binder,
            setup: {
                ...state.binder.setup,
                savingBinder: action.payload
            }
        }
    };
}

export function loadBinderStatusOptions(state, action) {
    if (!action || action.type !== LOAD_BINDER_STATUS_OPTIONS) {
        return state;
    }

    const newState = {
        ...state
    };
    newState.binderStatusOptions = action.payload;
    return newState;
}

export const setBinderNotifiedUsersLoadedStatusReducer = (state, action) => {
    if (action.type === SET_BINDER_NOTIFIED_USERS_LOADED_STATUS) {
        return {
            ...state,
            binderNotifiedUsers: {
                ...state.binderNotifiedUsers,
                loaded: action.payload
            }
        };
    }

    return state;
};

export const loadBinderNotifiedUsersSuccessReducer = (state, action) => {
    if (action.type === LOAD_BINDER_NOTIFIED_USERS_SUCCESS) {
        return {
            ...state,
            binderNotifiedUsers: {
                ...state.binderNotifiedUsers,
                users: action.payload
            }
        };
    }

    return state;
};

export const saveBinderNotifiedUsersSuccessReducer = (state, action) => {
    if (action.type === SAVE_BINDER_NOTIFIED_USERS_SUCCESS) {
        return {
            ...state,
            binderNotifiedUsers: {
                ...state.binderNotifiedUsers,
                users: action.payload
            }
        };
    }

    return state;
};

export default composeReducers(
    files,
    externalUsers,
    usersConfiguration,
    loadBinderUsers,
    loadBinder,
    removeBinder,
    removeUsers,
    mapInstitutionTimezone,
    loadDocument,
    binderErrors,
    loadBinderSetupFieldsData,
    notifyBinderSetupLoaded,
    updateBinderSetupFieldValue,
    writeBinderSetupErrors,
    writeBindersSaving,
    notifyBinderActivationSuccess,
    decrementUnfiledDocuments,
    setCountUnfiledDocuments,
    loadBinderStatusOptions,
    setBinderNotifiedUsersLoadedStatusReducer,
    loadBinderNotifiedUsersSuccessReducer,
    saveBinderNotifiedUsersSuccessReducer
);
